import React from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/lib/database.types';
import { RichTextContent } from '@/components/RichTextEditor';

type Producer = Database['public']['Tables']['producers']['Row'];

export function Producers() {
  const [producers, setProducers] = React.useState<Producer[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    async function fetchProducers() {
      const { data, error } = await supabase
        .from('producers')
        .select('*')
        .eq('is_approved', true)
        .order('company_name', { ascending: true });

      if (error) {
        console.error('Error fetching producers:', error);
        return;
      }

      setProducers(data || []);
      setLoading(false);
    }

    fetchProducers();
  }, []);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-8" />
          <div className="grid grid-cols-1 gap-y-10 sm:grid-cols-2 gap-x-6 lg:grid-cols-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="space-y-4">
                <div className="h-64 bg-gray-200 rounded-lg" />
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
      <h1 className="text-3xl font-extrabold text-gray-900 mb-8">Våra producenter</h1>
      {producers.length === 0 ? (
        <div className="text-center text-gray-500">
          Inga producenter hittades
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-y-10 sm:grid-cols-2 gap-x-6 lg:grid-cols-3">
          {producers.map((producer) => (
            <Link
              key={producer.id}
              to={`/producers/${producer.id}`}
              className="group"
            >
              <div className="relative h-64 w-full overflow-hidden rounded-lg bg-white group-hover:opacity-75">
                {producer.logo_url ? (
                  <img
                    src={producer.logo_url}
                    alt={producer.company_name}
                    className="h-full w-full object-contain bg-gray-50"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center bg-gray-100">
                    <span className="text-gray-400 text-2xl font-medium">
                      {producer.company_name.charAt(0)}
                    </span>
                  </div>
                )}
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                {producer.company_name}
              </h3>
              {producer.description && (
                <div className="mt-1 text-sm text-gray-500 line-clamp-3">
                  <RichTextContent 
                    content={
                      producer.description.length > 150 
                        ? producer.description.slice(0, 150) + '...' 
                        : producer.description
                    } 
                  />
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}